import React, { useState, useRef, useEffect } from 'react';
import { X, Check, ZoomIn, ZoomOut } from 'lucide-react';

interface ImageCropperProps {
  imageSrc: string;
  onCrop: (blob: Blob) => void;
  onCancel: () => void;
  cropShape?: 'circle' | 'rect';
  aspectRatio?: number; // width / height
}

const ImageCropper: React.FC<ImageCropperProps> = ({ 
  imageSrc, 
  onCrop, 
  onCancel,
  cropShape = 'circle',
  aspectRatio = 1
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const CANVAS_SIZE = 400;
  
  // Calcular dimensiones del área de recorte
  const getCropDimensions = () => {
    const padding = 40;
    const maxSize = CANVAS_SIZE - (padding * 2);
    
    if (cropShape === 'circle') {
      return { width: 250, height: 250 };
    }
    
    // Para rectángulo, ajustar al aspect ratio dentro del maxSize
    if (aspectRatio > 1) {
      return { width: maxSize, height: maxSize / aspectRatio };
    } else {
      return { width: maxSize * aspectRatio, height: maxSize };
    }
  };

  const { width: cropWidth, height: cropHeight } = getCropDimensions();

  useEffect(() => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      setImage(img);
      // Centrar imagen inicialmente y ajustar escala para cubrir el área de recorte
      const scaleX = cropWidth / img.width;
      const scaleY = cropHeight / img.height;
      const initialScale = Math.max(scaleX, scaleY);
      
      setScale(initialScale);
      setPosition({
        x: (CANVAS_SIZE - img.width * initialScale) / 2,
        y: (CANVAS_SIZE - img.height * initialScale) / 2
      });
    };
  }, [imageSrc, cropWidth, cropHeight]);

  useEffect(() => {
    draw();
  }, [image, scale, position, cropWidth, cropHeight]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Limpiar canvas
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Dibujar fondo oscuro
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Configurar operación para "recortar" el hueco
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    
    const centerX = CANVAS_SIZE / 2;
    const centerY = CANVAS_SIZE / 2;

    if (cropShape === 'circle') {
      ctx.arc(centerX, centerY, cropWidth / 2, 0, Math.PI * 2);
    } else {
      ctx.rect(centerX - cropWidth / 2, centerY - cropHeight / 2, cropWidth, cropHeight);
    }
    ctx.fill();

    // Volver a operación normal
    ctx.globalCompositeOperation = 'source-over';
    
    // Dibujar imagen transformada (se verá solo en el hueco si usamos clip, 
    // pero aquí usamos el truco de dibujar imagen y luego overlay con hueco)
    // Espera, el truco anterior era: 
    // 1. Limpiar
    // 2. Dibujar imagen
    // 3. Dibujar overlay con hueco
    
    // Reiniciamos para hacerlo bien
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    
    ctx.save();
    ctx.translate(position.x, position.y);
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0);
    ctx.restore();

    // Dibujar overlay (máscara)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.rect(0, 0, CANVAS_SIZE, CANVAS_SIZE); // Todo el canvas
    
    // Restar la forma central
    if (cropShape === 'circle') {
      ctx.arc(centerX, centerY, cropWidth / 2, 0, Math.PI * 2, true);
    } else {
      ctx.rect(centerX - cropWidth / 2, centerY - cropHeight / 2, cropWidth, cropHeight);
      // Para rect, necesitamos dibujar el rect en sentido contrario o usar clip.
      // El método de rect con winding rule es complejo si no usamos subpaths.
      // Alternativa simple: dibujar 4 rectángulos alrededor del área central.
    }
    
    if (cropShape === 'circle') {
      ctx.fill();
    } else {
      // Dibujar los 4 lados oscuros manualmente para el rectángulo
      const left = centerX - cropWidth / 2;
      const top = centerY - cropHeight / 2;
      const right = centerX + cropWidth / 2;
      const bottom = centerY + cropHeight / 2;
      
      ctx.fillRect(0, 0, CANVAS_SIZE, top); // Arriba
      ctx.fillRect(0, bottom, CANVAS_SIZE, CANVAS_SIZE - bottom); // Abajo
      ctx.fillRect(0, top, left, cropHeight); // Izquierda
      ctx.fillRect(right, top, CANVAS_SIZE - right, cropHeight); // Derecha
    }

    // Dibujar borde
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (cropShape === 'circle') {
      ctx.arc(centerX, centerY, cropWidth / 2, 0, Math.PI * 2);
    } else {
      ctx.rect(centerX - cropWidth / 2, centerY - cropHeight / 2, cropWidth, cropHeight);
    }
    ctx.stroke();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCrop = () => {
    if (!image) return;
    
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = cropWidth;
    outputCanvas.height = cropHeight;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) return;

    // Calcular offset relativo al centro del área de recorte
    const centerX = CANVAS_SIZE / 2;
    const centerY = CANVAS_SIZE / 2;
    
    const cropLeft = centerX - cropWidth / 2;
    const cropTop = centerY - cropHeight / 2;

    const offsetX = position.x - cropLeft;
    const offsetY = position.y - cropTop;

    if (cropShape === 'circle') {
      ctx.beginPath();
      ctx.arc(cropWidth / 2, cropHeight / 2, cropWidth / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0);

    outputCanvas.toBlob((blob) => {
      if (blob) onCrop(blob);
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div className="bg-white rounded-xl overflow-hidden shadow-2xl max-w-md w-full">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Ajustar Imagen</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 flex flex-col items-center">
          <div 
            className="relative overflow-hidden cursor-move border border-gray-200 bg-gray-100 rounded-lg"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <canvas 
              ref={canvasRef} 
              width={CANVAS_SIZE} 
              height={CANVAS_SIZE} 
              className="block"
            />
          </div>

          <div className="w-full mt-6 flex items-center gap-4">
            <ZoomOut className="w-5 h-5 text-gray-500" />
            <input
              type="range"
              min="0.1"
              max="3"
              step="0.1"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <ZoomIn className="w-5 h-5 text-gray-500" />
          </div>
          
          <p className="text-sm text-gray-500 mt-2">
            Arrastra para mover y usa el slider para hacer zoom
          </p>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleCrop}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Guardar Imagen
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
